import { create } from 'zustand';
import apiClient from '../lib/api';

export interface Timesheet {
  id: string;
  project_id: string;
  project_name: string;
  task_id: string | null;
  task_name: string | null;
  date: string;
  hours: number;
  description: string;
  status: string;
  created_at: string;
}

interface TimesheetState {
  timesheets: Timesheet[];
  isLoading: boolean;
  error: string | null;
  
  fetchTimesheets: (projectId?: string) => Promise<void>;
  createTimesheet: (data: any) => Promise<void>;
  updateTimesheet: (id: string, data: any) => Promise<void>;
  deleteTimesheet: (id: string) => Promise<void>;
}

export const useTimesheetStore = create<TimesheetState>((set, get) => ({
  timesheets: [],
  isLoading: false,
  error: null,

  fetchTimesheets: async (projectId?: string) => {
    set({ isLoading: true, error: null });
    try {
      const url = projectId ? `/work/timesheets?projectId=${projectId}` : '/work/timesheets';
      const response = await apiClient.get(url);
      set({ timesheets: response.data, isLoading: false });
    } catch (e: any) {
      set({ error: e.response?.data?.message || 'Failed to fetch timesheets', isLoading: false });
    }
  },

  createTimesheet: async (data: any) => {
    try {
      const response = await apiClient.post('/work/timesheets', data);
      
      // We refetch to get the joined project_name and task_name
      await get().fetchTimesheets(data.projectId);
    } catch (e: any) {
      throw new Error(e.response?.data?.message || 'Failed to log time');
    }
  },

  updateTimesheet: async (id: string, data: any) => {
    try {
      await apiClient.patch(`/work/timesheets/${id}`, data);
      await get().fetchTimesheets(data.projectId);
    } catch (e: any) {
      throw new Error(e.response?.data?.message || 'Failed to update timesheet');
    }
  },

  deleteTimesheet: async (id: string) => {
    try {
      await apiClient.delete(`/work/timesheets/${id}`);
      set(state => ({
        timesheets: state.timesheets.filter(t => t.id !== id)
      }));
    } catch (e: any) {
      throw new Error(e.response?.data?.message || 'Failed to delete timesheet');
    }
  }
}));
